import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';
import { casesData } from '@/mocks/casesData';

type FilterCategory = 'all' | 'campo' | 'oficina';

export default function CasesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilter = searchParams.get('filter') as FilterCategory | null;
  const [activeFilter, setActiveFilter] = useState<FilterCategory>(urlFilter || 'all');
  const [filteredCases, setFilteredCases] = useState(casesData.cases);

  useSeo({
    title: 'Cases de Sucesso | Pilar Manutenção Industrial - Projetos Reais',
    description: 'Conheça os cases de sucesso da Pilar em recuperação de equipamentos hidráulicos, mecânica industrial e serviços de campo. Projetos reais de indústrias de celulose, mineração, portos e siderurgia.',
    keywords: 'cases de manutenção industrial, recuperação de equipamentos, projetos hidráulica industrial, serviços de campo, Grupo Pilar, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/cases',
    ogUrl: 'https://www.pilarmanutencao.com.br/cases',
    ogImage: ASSETS.logoPilar,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredCases(casesData.cases);
      if (searchParams.has('filter')) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('filter');
        setSearchParams(newParams, { replace: true });
      }
    } else {
      setFilteredCases(casesData.cases.filter((c) => c.category === activeFilter));
      if (searchParams.get('filter') !== activeFilter) {
        setSearchParams({ filter: activeFilter }, { replace: true });
      }
    }
  }, [activeFilter, searchParams, setSearchParams]);

  useEffect(() => {
    const validFilters: FilterCategory[] = ['all', 'campo', 'oficina'];
    if (urlFilter && validFilters.includes(urlFilter)) {
      setActiveFilter(urlFilter);
    }
  }, [urlFilter]);

  const filterOptions: { value: FilterCategory; label: string }[] = [
    { value: 'all', label: 'Todos os Cases' },
    { value: 'campo', label: 'Serviços de Campo' },
    { value: 'oficina', label: 'Oficina Especializada' },
  ];

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/CASES/HERO/HERO-CASES.webp"
              alt="Cases de sucesso da Pilar Manutenção Industrial"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-8 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Projetos Executados</span>
                <div className="w-8 h-[2px] bg-gp-yellow"></div>
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase mb-6">
                Cases de <span className="text-gp-yellow">Sucesso</span>
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Conheça os cases de sucesso da Pilar em manutenção industrial, serviços de campo e recuperação de equipamentos. Resultados documentados de indústrias de celulose, mineração, portos e siderurgia.
              </p>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="relative py-8 bg-gp-graphite-light border-b border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-wrap items-center justify-center gap-3">
              {filterOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setActiveFilter(option.value)}
                  className={`px-6 py-3 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeFilter === option.value
                      ? 'bg-gp-yellow text-gp-graphite'
                      : 'bg-gp-graphite border border-gp-graphite-lighter text-gray-300 hover:border-gp-yellow/30 hover:text-gp-yellow'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Cases Grid */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCases.map((caseItem) => (
                <article
                  key={caseItem.id}
                  className="group bg-gp-graphite border border-gp-graphite-lighter rounded-lg overflow-hidden hover:border-gp-yellow/30 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={caseItem.cardImage}
                      alt={caseItem.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gp-graphite via-transparent to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className={`inline-block px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider ${
                        caseItem.category === 'oficina'
                          ? 'bg-gp-yellow text-gp-graphite'
                          : 'bg-gp-blue text-white'
                      }`}>
                        {caseItem.categoryLabel}
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="text-gray-400 text-xs uppercase tracking-wider">{caseItem.clientSegment}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-bold text-white uppercase mb-3 group-hover:text-gp-yellow transition-colors duration-300 line-clamp-2">
                      {caseItem.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-5 line-clamp-3">
                      {caseItem.shortDesc}
                    </p>
                    <Link
                      to={`/${caseItem.slug}`}
                      className="inline-flex items-center gap-2 text-gp-yellow hover:text-gp-yellow-dark text-sm font-semibold uppercase tracking-wider transition-colors duration-300"
                    >
                      Ver Case Completo
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            {filteredCases.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-gp-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-folder-open-line text-gp-yellow text-2xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <p className="text-gray-400 text-lg">Nenhum case encontrado para esta categoria.</p>
                <button
                  onClick={() => setActiveFilter('all')}
                  className="mt-4 text-gp-yellow hover:text-gp-yellow-dark text-sm font-semibold uppercase tracking-wider transition-colors duration-300 cursor-pointer"
                >
                  Ver todos os cases
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Quer transformar seu projeto em um Case de Sucesso?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Entre em contato com a Pilar e descubra como podemos ajudar sua indústria com serviços de recuperação, manutenção e apoio técnico especializado.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://wa.me/5527993092922"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span>Solicitar Orçamento</span>
                    <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <Link
                    to="/contato"
                    className="inline-flex items-center justify-center gap-2 border border-gray-500 hover:border-gp-yellow text-gray-300 hover:text-gp-yellow px-8 py-4 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-mail-line text-lg w-6 h-6 flex items-center justify-center"></i>
                    Falar com Especialista
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Home */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para a Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}